import { withCreds } from '../authService';

const base = '/api/conversations';
async function decode(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || 'Conversation request failed');
  return body;
}
export const listConversationThreads = () => withCreds(base).then(decode);
export const selectConversationToResume = (threads = []) => {
  const available = Array.isArray(threads) ? threads.filter((thread) => thread?.id) : [];
  const timestamp = (value) => {
    const parsed = Date.parse(value || '');
    return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
  };
  // Prefer the conversation most recently used even if an intermediary or
  // cache returns the authorized list out of order. Creation time is the
  // deterministic fallback for threads whose updates tie.
  return available
    .map((thread, index) => ({ thread, index }))
    .sort((left, right) => (
      timestamp(right.thread.updated_at) - timestamp(left.thread.updated_at)
      || timestamp(right.thread.created_at) - timestamp(left.thread.created_at)
      || left.index - right.index
    ))[0]?.thread || null;
};
export const listArchivedConversationThreads = () => withCreds(base + '/archived').then(decode);
export const createConversationThread = (title) => withCreds(base, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title }),
}).then(decode);
export const loadConversationMessages = (id) => withCreds(base + '/' + encodeURIComponent(id) + '/messages').then(decode);
export const editConversationMessage = (threadId, messageId, text) => withCreds(
  base + '/' + encodeURIComponent(threadId) + '/messages/' + encodeURIComponent(messageId), {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
  },
).then(decode);
export const deleteConversationThread = async (id) => {
  const response = await withCreds(base + '/' + encodeURIComponent(id), { method: 'DELETE' });
  if (!response.ok) throw new Error('Could not archive conversation');
};
export const restoreConversationThread = (id) => withCreds(
  base + '/' + encodeURIComponent(id) + '/restore', { method: 'POST' },
).then(decode);
export const permanentlyDeleteConversationThread = async (id) => {
  const response = await withCreds(base + '/' + encodeURIComponent(id) + '/permanent', {
    method: 'DELETE', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirmation: 'DELETE_ARCHIVED_CONVERSATION' }),
  });
  if (!response.ok) throw new Error('Could not permanently delete conversation');
};
export const setCrossThreadMemory = (id, enabled) => withCreds(base + '/' + encodeURIComponent(id) + '/settings', {
  method: 'PATCH', headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ cross_thread_memory_enabled: enabled }),
}).then(decode);
export const getConversationPreferences = () => withCreds(base + '/preferences').then(decode);
export const getConversationRetentionStatus = () => withCreds(base + '/status').then(decode);
export const updateConversationPreferences = (preferences) => withCreds(base + '/preferences', {
  method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(preferences),
}).then(decode);
export const deleteAllConversationThreads = async () => {
  const response = await withCreds(base, {
    method: 'DELETE', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirmation: 'DELETE_ALL_CONVERSATIONS' }),
  });
  if (!response.ok) throw new Error('Could not delete conversations');
};

export const downloadAccountData = async () => {
  const response = await withCreds('/privacy/export');
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || 'Could not export account data');
  const blob = new Blob([JSON.stringify(body, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'brightresolve-account-data.json';
  link.click();
  URL.revokeObjectURL(url);
};

export const deleteAccountAndData = async (currentPassword) => {
  const response = await withCreds('/privacy/forget', {
    method: 'DELETE', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirmation: 'DELETE_MY_ACCOUNT', current_password: currentPassword }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || 'Could not delete account');
};
